import { ChangeEvent, SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { AggregationsDto, ColumnMetricDto, DataGroupDto, FilterMetricDto, FilterOperatorsDto, GenerateReportRequest, LogicalOperatorDto, OrderTypesResponse, PrimaryCategory2, Reason, ReportColumnDto, ReportDto, ReportFilterGroupDto, ReportFilterGroupFilterDto, ReportFilterGroupLinkDto, Store } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useNavigate, useParams, useLocation, useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { UserContext } from '../../../components/shared/useUser'; 
import { TypesRecord } from '../../../pages/Transactions/types/TypesRecord';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { useMediaQuery } from '@mui/material';
import { theme } from '../../../theme';         
import ReportValidation   from '../utils/reportValidation';   

export const useReportsBuilder = () => { 
  const { t } = useTranslation('reportBuilder');
  const { getFilterOperator, getFilterMetrics, getColumnMetrics, getLogicalOperators, getDataGroups, saveReport, getUpdateReport,getReportAggregations,
    updateReport, generateReport,getPrimaryCategoriesForStore, getOperatorsForMetric,getConfiguration, getTransactionTypes,getTransactionReasons,getOrderTypes } = useNswagClient();
 
    type GroupReasons = {
  [groupId: number]: Reason[] | undefined;  
};
    const navigate = useNavigate();
    const { logError } = useLogError();
    const { id } = useParams<{ id: string | undefined }>();
   
    const { selectedStore, user, hasPermissionTo, isAdmin } = useContext(UserContext);
    const [dataGroups, setDataGroups] = useState<DataGroupDto[]>([]);
    const [selectedDataGroup, setSelectedDataGroup] = useState<number | undefined>(undefined);
    const [allColumnMetrics, setAllColumnMetrics] = useState<ColumnMetricDto[]>();
    const [allFilterMetrics, setAllFilterMetrics] = useState<FilterMetricDto[]>();
    const [filterMetrics, setFilterMetrics] = useState<FilterMetricDto[]>();
    const [columnMetrics, setColumnMetrics] = useState<ColumnMetricDto[]>();
    const [logicalOperator, setLogicalOperator] = useState<LogicalOperatorDto[]>();
    const [filterOperator, setFilterOperator] = useState<FilterOperatorsDto[]>();
    const [filterGroups, setFilterGroups] = useState<ReportFilterGroupDto[]>([]);
    const [reportAggregations, setReportAggregations] = useState<AggregationsDto[]>([]);
    const [reportName, setReportName] = useState<string>(' ');
    const [columns, setColumns] = useState<ReportColumnDto[]>([]);
    const [report, setReport] = useState<ReportDto>();
    const [filterGroupLinks, setFilterGroupLinks] = useState<ReportFilterGroupLinkDto[]>([]); 
    const reportId = id ? parseInt(id) : undefined;
    const [loading, setLoading] = useState<boolean>(false); 
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [disableCreateReport, setDisableCreateReport] = useState<boolean>(true);
    const [informationMessage, setInformationMessage] = useState('');
    const [informationModalTitle, setInformationModalTitle] = useState('');
    const [openInformationDialog, setOpenInformationDialog] = useState(false);
    const [openSaveReportModal, setOpenSaveReportModal] = useState(false);
    const [canNotSaveMessage, setCanNotSaveMessage] = useState('');
    const [openGoBackDialog, setOpenGoBackDialog] = useState(false);
    const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
    const [selectedStores, setSelectedStores] = useState<Store[]>([]); 
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [reportAggregationId, setReportAggregationId] = useState<number | undefined>(0);
    const[checkedAllStores,setCheckedAllStores] = useState<boolean>(false);
    const [allCategories, setAllCategories] = useState<PrimaryCategory2[]>([]);
    const [nextUiId, setNextUiId] =useState(1); 
    const [transactionTypes, setTransactionTypes] = useState<TypesRecord[]>([]);
    const [ordersType, setOrdersType] = useState<OrderTypesResponse>({});
    const [reasons, setReasons] = useState<Reason[] >([]);
    const [filterOperatorsMap, setFilterOperatorsMap] = useState<Map<number, FilterOperatorsDto[]>>(new Map());
    const [hasChanges, setHasChanges] = useState<boolean>(false); 
    const [isError, setIsError] = useState<boolean>(false); 
    const [groupReasons, setGroupReasons] = useState<GroupReasons>({});  
    const [invalidFilters, setInvalidFilters] = useState<{ groupId: number; filterId?: number; field: FilterField; message?: string }[]>([]);
    const [isSnackBarOpen, setIsSnackBarOpen] = useState(false); 
    const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'success'>('error');
    const [snackbarMessage, setSnackbarMessage] = useState(''); 
    const [fromDateError, setFromDateError] = useState(false);
    const [toDateError, setToDateError] = useState(false);
    const [hasFilterConflict, setHasFilterConflict] = useState(false); 
    const [didInitColumns, setDidInitColumns] = useState(false); 
    const isNavigatingRef = useRef(false);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isGenerateDisabled = !fromDate || !toDate || (!checkedAllStores && selectedStores.length === 0);

    type FilterField = 'Metric' | 'Operator' | 'Value' | 'LogicalOperator' | 'GroupLogicalOperator';
    const location = useLocation(); 
    const duplicatedReport = location.state?.duplicatedReport;
    const isDuplicated: boolean = location.state?.isDuplicated;
 

    useEffect(() => {
      getAggregationsList();
      getFilterOperatorList();
      getFilterMetricsList();
      getLogicalOperatorsList();
      getDataGroupsList();
      setReportAggregationId(1);

      setReport(prev => ({
        ...prev,
        aggregationId: prev?.aggregationId ?? 1,
      }));

      loadCategories(selectedStore?.storeNumber!);
      loadTransactionTypes(selectedStore?.storeNumber);
      getReasons(undefined);
      loadOrderTypes();

     

      if (duplicatedReport) {
        setReportData(duplicatedReport);
        setReportName(duplicatedReport.reportName);
        setIsEdit(false);
      }

      if (reportId !== 0) {
        fetchReportData();
        setIsEdit(true);
      } else { 
        setIsEdit(false);
        getColumnMetricsList(1);

        setReport((prevReport) => ({
          ...prevReport,
          storeNumber: selectedStore?.storeNumber!,
          reportName: duplicatedReport ? duplicatedReport.reportName : reportName,
        }));
      }
    }, [reportId]);


    // Fetch operators when report is loaded and metrics are available
    useEffect(() => {
      const fetchOperatorsForReport = async () => {
        if (!report || !allFilterMetrics) return;
        
        const operatorsMap = new Map(filterOperatorsMap);
        const uniqueMetrics = new Map<number, FilterMetricDto>();

        // Collect unique metrics from all filter groups that don't have operators loaded yet
        for (const group of report.reportFilterGroups ?? []) {
          for (const filterGroupFilter of group.reportFilterGroupFilters ?? []) {
            const metricId = filterGroupFilter.filter?.filterMetricId;
            if (!metricId || operatorsMap.has(metricId)) continue; // Skip if already loaded
            
            const metric = allFilterMetrics.find(m => m.id === metricId);
            if (metric) {
              uniqueMetrics.set(metricId, metric);
            }
          }
        }

        // Only fetch if there are new metrics to load
        if (uniqueMetrics.size === 0) return;

        // Fetch all operators in parallel
        const operatorPromises = Array.from(uniqueMetrics.entries()).map(async ([metricId, metric]) => {
          try {
            const response = await getOperatorsForMetric(metric.fieldType!);
            return { metricId, operators: response.data || [] };
          } catch (error) {
            logError(error);
            return { metricId, operators: [] };
          }
        });

        const results = await Promise.all(operatorPromises);
        results.forEach(({ metricId, operators }) => {
          operatorsMap.set(metricId, operators);
        });

        setFilterOperatorsMap(operatorsMap);
      };

      fetchOperatorsForReport();
    }, [report, allFilterMetrics]);

    useEffect(() => {
      const selectedMetric = filterMetrics?.find(option => option.name === 'Transaction Type');

      filterGroups?.forEach(group => {
        const transactionTypeFilter = group.reportFilterGroupFilters?.find(filterGroupFilter => {
          return filterGroupFilter?.filter?.filterMetricId === selectedMetric?.id;
        });

        const transactionTypeValue = transactionTypeFilter?.filter?.value;

        if (transactionTypeValue) {
          if (group.id) { 
            getReasons(group.id);  
          }
        }
        const metricsMap = new Map<number, FilterMetricDto>();
        filterMetrics?.forEach((m) => {
          if (m.id != null) metricsMap.set(m.id, m);
        }); 
        filterMetrics?.forEach(m => m.id && metricsMap.set(m.id, m));

        const columnsMap = new Map<number, ColumnMetricDto>();
        columnMetrics?.forEach(m => m.id && columnsMap.set(m.id, m));
        const validation = ReportValidation.validateReport(
    report!,
    metricsMap,
    columnsMap, 
    filterOperatorsMap,
    logicalOperator,
    filterGroupLinks,
        );

        const conflict =  !validation.isFilterValid; 

        setHasFilterConflict(conflict ?? false);
        
        // Clear old conflict errors and add new ones if conflicts exist
        setInvalidFilters(prev => {
          // Remove all previous conflict errors (check by comparing with translated message)
          const conflictMessage = t('conflictingFilterLogic');
          const nonConflictErrors = prev.filter(e => e.message !== conflictMessage);
          
          // If there's a filter conflict, detect and mark the conflicting filters
          if (conflict && filterGroups) {
            const andOperatorId = logicalOperator?.find(op => op.name?.toUpperCase() === 'AND')?.id;
            const conflictErrors: { groupId: number; filterId?: number; field: FilterField; message?: string }[] = [];
            
            // Check each group for internal conflicts
            filterGroups.forEach((group) => {
              const filters = group.reportFilterGroupFilters ?? [];
              const andFilters = filters.filter(f => {
                const logicId = f.logicalOperatorId;
                return logicId == null || logicId === andOperatorId;
              });
              
              // Group filters by metric
              const metricFilters = new Map<number, typeof andFilters>();
              andFilters.forEach(f => {
                const metricId = f.filter?.filterMetricId;
                if (metricId) {
                  if (!metricFilters.has(metricId)) metricFilters.set(metricId, []);
                  metricFilters.get(metricId)!.push(f);
                }
              });
              
              // Check for conflicts within each metric
              metricFilters.forEach((filtersForMetric, metricId) => {
                const ops = filterOperatorsMap.get(metricId) ?? [];
                
                const equals = filtersForMetric.filter(f => {
                  const opId = f.filter?.operatorId;
                  if (!opId) return false;
                  const op = ops.find(o => o.id === opId);
                  return op?.description?.toUpperCase() === 'EQUAL';
                });
                
                const notEquals = filtersForMetric.filter(f => {
                  const opId = f.filter?.operatorId;
                  if (!opId) return false;
                  const op = ops.find(o => o.id === opId);
                  return op?.description?.toUpperCase() === 'NOT EQUAL';
                });
                
                // Check for multiple EQUAL with different values
                if (equals.length > 1) {
                  const values = equals.map(f => f.filter?.value?.trim().toLowerCase()).filter(Boolean);
                  const uniqueValues = new Set(values);
                  if (uniqueValues.size > 1) {
                    // Conflicting EQUAL values - mark all as conflicting
                    equals.forEach(f => {
                      if (f.filter?.uiId && group.id) {
                        conflictErrors.push({
                          groupId: group.id,
                          filterId: f.filter.uiId,
                          field: 'Value',
                          message: 'Conflicting filter logic',
                        });
                      }
                    });
                  }
                }
                
                // Check for EQUAL vs NOT EQUAL with same value
                if (equals.length > 0 && notEquals.length > 0) {
                  const equalValues = new Set(equals.map(f => f.filter?.value?.trim().toLowerCase()).filter(Boolean));
                  const notEqualValues = new Set(notEquals.map(f => f.filter?.value?.trim().toLowerCase()).filter(Boolean));
                  
                  // Check if any value appears in both EQUAL and NOT EQUAL
                  const hasConflict = Array.from(equalValues).some(val => notEqualValues.has(val));
                  
                  if (hasConflict) {
                    // Mark all EQUAL and NOT EQUAL filters with conflicting values
                    [...equals, ...notEquals].forEach(f => {
                      const value = f.filter?.value?.trim().toLowerCase();
                      if (value && equalValues.has(value) && notEqualValues.has(value) && f.filter?.uiId && group.id) {
                        conflictErrors.push({
                          groupId: group.id,
                          filterId: f.filter.uiId,
                          field: 'Value',
                          message: 'Conflicting filter logic',
                        });
                      }
                    });
                  }
                }
              });
            });
            
            // Return non-conflict errors plus new conflict errors
            return [...nonConflictErrors, ...conflictErrors];
          }
          
          // No conflicts - just return non-conflict errors
          return nonConflictErrors;
        });
      });
    }, [filterGroups, filterMetrics]); 

    useEffect(() => {
      // Only fetch metrics when loading an EXISTING report (reportId !== 0) and aggregations are available
      // This ensures we load the correct metrics when opening a saved report
      // When creating a NEW report (reportId === 0), metrics are loaded by the initial useEffect or handleAggregateChange
      if (reportAggregations.length > 0 && reportId !== 0 && report?.aggregationId) { 
        const selectedAggregation = reportAggregations.find(agg => agg.id === report.aggregationId);
        if (selectedAggregation && typeof selectedAggregation.id === 'number') {
          getColumnMetricsList(selectedAggregation.id);
        }
      }
    }, [reportAggregations, reportId]); // Only re-run if reportAggregations or reportId changes (not on every render)

    useEffect(() => {
      setDisableCreateReport(!isRequestValid());
   
    }, [columns, columnMetrics]); 
 
    // Re-filter metrics when data group selection changes (critical for edit mode)
    useEffect(() => {
      if (selectedDataGroup && allColumnMetrics) {
        
        // First filter by data group
        let filtered = allColumnMetrics.filter(
          (metric) => metric.isDefaultField || metric.dataGroupId === selectedDataGroup,
        );
        
        
        // Then apply aggregation filtering (same logic as in getColumnMetricsList)
        const rawOnlyMetrics = [
          'Opening Stock on Hand',
          'Closing Stock on Hand',
          'Theoretical Usage Quantity',
          'Actual Usage Quantity',
          'Variance - Cost Price',
          'Variance - Quantity',
        ];
        const rawExclusiveMetrics = ['Last Approved Stock on Hand'];
        const rawOrDailyOnlyMetrics = ['Date'];
        const startEndDateMetrics = ['Start Date', 'End Date'];
        
        const selectedAggregation = reportAggregations.find(agg => agg.id === reportAggregationId);
        const aggregationName = selectedAggregation?.name?.toLowerCase();
        const isRaw = aggregationName === 'raw';
        const isDailyTotal = aggregationName === 'daily total';
        const isTotal = aggregationName === 'total';
        
        // Apply aggregation-based filtering
        if (!isRaw) {
          filtered = filtered.filter(metric =>
            !rawOnlyMetrics.includes(metric.name!) &&
            !rawExclusiveMetrics.includes(metric.name!),
          );
        }
        
        if (isRaw || isDailyTotal || isTotal) {
          filtered = filtered.filter(metric =>
            !startEndDateMetrics.includes(metric.name!),
          );
        }
        
        if (!isRaw && !isDailyTotal) {
          filtered = filtered.filter(metric =>
            !rawOrDailyOnlyMetrics.includes(metric.name!),
          );
        }
        
        // If mapping not populated on backend, avoid leaving user with an empty list.
        // Fallback: show default fields and inform the user that metric mappings may be missing.
        if (filtered.length === 0) {
          const defaults = allColumnMetrics.filter(m => m.isDefaultField);
          setColumnMetrics(defaults);
          setSnackbarMessage(t('noMetricsForDataGroup'));
          setSnackBarSeverity('warning');
          setIsSnackBarOpen(true);
        } else {
          setColumnMetrics(filtered);
        }
      }
      
      if (selectedDataGroup && allFilterMetrics) {
        const excludedDateFields = ['date', 'planned count date', 'delivery date', 'start date', 'end date'];
        const filtered = allFilterMetrics.filter(
          (metric) => !excludedDateFields.includes(metric.name?.toLowerCase() ?? '') && (metric.isDefaultField || metric.dataGroupId === selectedDataGroup),
        );
        if (filtered.length === 0) {
          const defaults = allFilterMetrics.filter(m => m.isDefaultField && !excludedDateFields.includes(m.name?.toLowerCase() ?? ''));
          setFilterMetrics(defaults);
        } else {
          setFilterMetrics(filtered);
        }
      }
    }, [selectedDataGroup, allColumnMetrics, allFilterMetrics]);
 
    useEffect(() => {
      // Don't auto-add default columns for a new report until a data group has been selected.
      // Re-add default columns when data group changes (didInitColumns is reset in handleDataGroupChange)
      if (!allColumnMetrics || isEdit || didInitColumns || !selectedDataGroup) return;
      
      // Skip if columns already exist (unless they were just cleared by data group change)
      if (columns.length > 0) return;

      // Filter metrics for the current data group (including default fields)
      const metricsForDataGroup = allColumnMetrics.filter(
        m => m.isDefaultField || m.dataGroupId === selectedDataGroup,
      );

      const defaultColumns: ReportColumnDto[] = [];
      let localUiId = nextUiId;


      // Date is specific to Transactions (DataGroupId = 1), so only add it if it exists for this data group
      const dateMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'date');
      if (dateMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: dateMetric.id });
      }

      // Delivery Date - add if available for this data group
      const deliveryDateMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'delivery date');
      if (deliveryDateMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: deliveryDateMetric.id });
      }

      // Last Transaction Date - add if available for this data group
      const lastTransactionDateMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'last transaction date');
      if (lastTransactionDateMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: lastTransactionDateMetric.id });
      }

      // Planned Count Date - add if available for this data group
      const plannedCountDateMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'planned count date');
      if (plannedCountDateMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: plannedCountDateMetric.id });
      }

      const storeNameMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'store name');
      if (storeNameMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: storeNameMetric.id });
      }

      const skuMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'starbucks sku');
      if (skuMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: skuMetric.id });
      }

      const itemDescriptionMetric = metricsForDataGroup.find(m => m.name?.toLowerCase() === 'item description');
      if (itemDescriptionMetric) {
        defaultColumns.push({ id: localUiId++, reportId: 0, metricId: itemDescriptionMetric.id });
      }

      defaultColumns.push({ id: localUiId++, reportId: 0, metricId: 0 });

      setColumns(defaultColumns);
      setReport(prev => ({
        ...prev,
        reportColumns: defaultColumns,
      }));
      setNextUiId(localUiId);
      setDidInitColumns(true);
    }, [allColumnMetrics, isEdit, columns.length, didInitColumns, selectedDataGroup]);


  
    const fetchReportData = () => {
      setLoading(true);
      getUpdateReport(reportId ?? 0)
        .then((result) => {
          if (result.data) {
            setReportData(result.data);
          }
        })
        .catch(e => {
          logError(e);
        })
        .finally(() => setLoading(false));
    };

    const openGenerate = () =>{
      if (!validateFilters()) {
        setSnackBarProps('error', t('validationErrorRequiredFilters')); 
        return;
      }
      setOpenGenerateDialog(true);
    };

    const handleAddFilterGroup = () => {
      addFilterGroup();
      if (filterGroups.length > 0) {
        const lastGroup = filterGroups[filterGroups.length - 1];
        const newLink: ReportFilterGroupLinkDto = {
          id: 0,
          reportId: lastGroup.reportId!,
          group1Id: lastGroup.id!,
          group2Id: filterGroups.length + 1,
          logicalOperatorId: 1,
        };
        setFilterGroupLinks([...filterGroupLinks, newLink]);
      }
    };

    const loadOrderTypes = ()=>{
      const franchiseName = selectedStore?.franchiseName ?? '';
      getOrderTypes(franchiseName).then((result) => {
        if (result?.data) {
          setOrdersType(result.data);
        }
      })
        .catch((error) => {
          logError(error);
        });
    };
 
    const loadTransactionTypes = async (storeNum?: string): Promise<TypesRecord[]> => {
      try {
        const storeNumToUse = storeNum; 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const configResponse : any = await getConfiguration(ConfigurationName.ReturnToVendorEnabled, storeNumToUse!, selectedStore?.franchiseName ?? '');
        const element = await getTransactionTypes(storeNumToUse!);
        let types: TypesRecord[] = [];
        element.data?.forEach(x => {
          if (x.displayName) {
            const type = {
              id: '',
              displayName: x.displayName,
              type: x.displayName ?? '',
              franchiseID: x.franchiseId ?? -1,
              direction: x.direction ?? '',
            };
            types = types.concat(type);
          }
        }); 
        
        const stockAdjustments: TypesRecord = { id: '',displayName: t('transactions.types.Stock Adjustment'), type: 'Stock Adjustment', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
        const returnToStock: TypesRecord = { id: '',displayName:t('transactions.types.Return to Stock'), type: 'Return to Stock', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
        const madeInStore: TypesRecord = { id: '',displayName:t('transactions.types.Made in Store'), type: 'Made in Store', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
        const returnToVendor: TypesRecord = { id: '',displayName:t('transactions.types.Return to Vendor'), type: 'Return to Vendor', franchiseID: -1, direction: '', enabled: undefined, enabledDuplicateValidation: undefined };
  
        types = types.concat(stockAdjustments, returnToStock);

        if(configResponse.data && configResponse.data.value === 'true'){
          types = types.concat(returnToVendor);
        }
        const hasMadeInStorePerm = hasPermissionTo(['MadeInStoreRead']);
        if (hasMadeInStorePerm) {
          types = types.concat(madeInStore);
        }

        types.forEach((rag, index) => rag.id = (index + 1).toString());
        setTransactionTypes(types);
        return types;
      } catch (error) {
        console.error('Error:', error);
      }
      return [];
    };
 
 
    const isRequestValid = (): boolean => {
      if (reportName.trim() === '') return false;
      
      // Data group must be selected
      if (!selectedDataGroup) return false;

      if (columns.length === 0 || !columnMetrics || columnMetrics.length === 0) return false;
   
      const columnMetricIds = new Set(columnMetrics.map(metric => metric.id));
 
      return columns.some(column => column.metricId !== 0 && columnMetricIds.has(column.metricId));
    };
    const setReportData = async (report: ReportDto) => {
      let newUiId = nextUiId;
    report.reportFilterGroups!.forEach(group => {
      group.reportFilterGroupFilters!.forEach(filterGroupFilter => {
        filterGroupFilter.filter!.uiId = newUiId;
        newUiId += 1;
      });
    });
    setNextUiId(newUiId);
    
    // Set the data group from the loaded report
    if (report.dataGroupId) {
      setSelectedDataGroup(report.dataGroupId);
    }
    
    setFilterGroupLinks(report.reportFilterGroupLinks!);
    setReportName(report.reportName!);
    setFilterGroups(report.reportFilterGroups!);
    setColumns([
      ...(report.reportColumns ?? []),
      { id: newUiId, reportId: report.id!, metricId: 0 },
    ]);  
    setDisableCreateReport(false); 
    getAggregationsList();
    setReportAggregationId(report.aggregationId);
    setReport(report);
    };
 

    // Removed: Duplicate function - logic now integrated in setReportData with parallel fetching 

    const getFilterOperatorList = () => {
      getFilterOperator()
        .then((result) => {
          if (result.data) {
            setFilterOperator(result.data);
          }
        })
        .catch((error) => {
          logError(error);
        });
    };

    const getColumnMetricsList = (selectedAggregationId: number, dataGroupId?: number) => {
      getColumnMetrics()
        .then((result) => {
          if (!result.data) return; 
          
          // Store all metrics
          setAllColumnMetrics(result.data);
          
          const rawOnlyMetrics = [
            'Opening Stock on Hand',
            'Closing Stock on Hand',
            'Theoretical Usage Quantity', 
            'Actual Usage Quantity',
            'Variance - Cost Price',
            'Variance - Quantity'];
          const rawExclusiveMetrics = ['Last Approved Stock on Hand'];
          const rawOrDailyOnlyMetrics = ['Date'];
          const startEndDateMetrics = ['Start Date', 'End Date'];

          const selectedAggregation = reportAggregations.find(agg => agg.id === selectedAggregationId);
          const aggregationName = selectedAggregation?.name?.toLowerCase();
          
          const isRaw = aggregationName === 'raw';
          const isDailyTotal = aggregationName === 'daily total';
          const isTotal = aggregationName === 'total';

          // Start with all metrics that have a name
          let filteredMetrics = result.data.filter(metric => metric.name);

          // Apply data group filtering first (if applicable)
          const groupToApply = dataGroupId ?? selectedDataGroup;
          if (groupToApply) {
            filteredMetrics = filteredMetrics.filter(metric => 
              // Show metrics that belong to the selected data group
              metric.dataGroupId === groupToApply,
            );
          }

          // Then apply aggregation-based filtering (this ensures raw-only metrics stay out)
          if (!isRaw) {
            // For non-Raw aggregations, filter out raw-only metrics
            filteredMetrics = filteredMetrics.filter(metric =>
              !rawOnlyMetrics.includes(metric.name!) &&
              !rawExclusiveMetrics.includes(metric.name!),
            );
          }

          // Filter out Start/End Date for Raw, Daily Total, and Total
          if (isRaw || isDailyTotal || isTotal) {
            filteredMetrics = filteredMetrics.filter(metric =>
              !startEndDateMetrics.includes(metric.name!),
            );
          }

          // Filter out Date field for aggregations that aren't Raw or Daily Total
          if (!isRaw && !isDailyTotal) {
            filteredMetrics = filteredMetrics.filter(metric =>
              !rawOrDailyOnlyMetrics.includes(metric.name!),
            );
          }

          
          setColumnMetrics(filteredMetrics);

          // Auto-add Start Date and End Date when switching to aggregations that need them
          const needsStartEndDate = !isRaw && !isDailyTotal;  // Weekly Total, Monthly Total, Total
          if (needsStartEndDate && columns) {
            const hasDateColumn = columns.some(col => {
              const metricName = allColumnMetrics?.find(metric => metric.id === col.metricId)?.name;
              return metricName === 'Date';
            });

            if (hasDateColumn) {
              const updatedColumns = [...columns];
              const hasStartDate = updatedColumns.some(col => {
                const metricName = filteredMetrics.find(metric => metric.id === col.metricId)?.name;
                return metricName === 'Start Date';
              });
              const hasEndDate = updatedColumns.some(col => {
                const metricName = filteredMetrics.find(metric => metric.id === col.metricId)?.name;
                return metricName === 'End Date';
              });

              // Add Start Date if not present
              if (!hasStartDate) {
                const startDateMetric = filteredMetrics.find(metric => metric.name === 'Start Date');
                if (startDateMetric) {
                  updatedColumns.push({ metricId: startDateMetric.id! });
                }
              }

              // Add End Date if not present
              if (!hasEndDate) {
                const endDateMetric = filteredMetrics.find(metric => metric.name === 'End Date');
                if (endDateMetric) {
                  updatedColumns.push({ metricId: endDateMetric.id! });
                }
              }

              // Update columns state if we added any fields
              if (updatedColumns.length > columns.length) {
                setColumns(updatedColumns);
              }
            }
          }
        })
        .catch((error) => {
          logError(error);
        });
    };

  
    const getFilterMetricsList = () => {
      getFilterMetrics()
        .then((result) => {
          if (result.data) {
            // Store all filter metrics
            setAllFilterMetrics(result.data);
            
            // Filter by data group if selected and exclude date fields (user selects dates when generating report)
            const excludedDateFields = ['date', 'planned count date', 'delivery date', 'start date', 'end date'];
            let filteredMetrics = result.data.filter(metric => 
              !excludedDateFields.includes(metric.name?.toLowerCase() ?? ''),
            );
            if (selectedDataGroup) {
              filteredMetrics = filteredMetrics.filter(metric => 
                metric.isDefaultField || metric.dataGroupId === selectedDataGroup,
              );
            }
            
            setFilterMetrics(filteredMetrics);
          }
        })
        .catch((error) => {
          logError(error);
        });
    };

    const getLogicalOperatorsList = () => {
      getLogicalOperators()
        .then((result) => {
          if (result.data) {
            setLogicalOperator(result.data);
          }
        })
        .catch((error) => {
          logError(error);
        });
    };

    const getAggregationsList = () => {
      getReportAggregations()
        .then((result) => {
          if (result.data) {  
            setReportAggregations(result.data);
          }
        })
        .catch((error) => {
          logError(error);
        });
    }; 

    const getDataGroupsList = () => {
      getDataGroups()
        .then((result) => {
          if (result.data) {  
            setDataGroups(result.data);
          }
        })
        .catch((error) => {
          logError(error);
        });
    };

    const getMetricNameById = (metricId: number) => {
      const metric = columnMetrics?.find(option => option.id === metricId);
      return metric ? metric.name : '';
    }; 
    
       
     
 
    const navigateToReports = () =>{
      isNavigatingRef.current = true;
      setHasChanges(false);
      localStorage.setItem('activeTab', '2');
      localStorage.setItem('activeSubTab', isAdmin() ? '1' : '0');
      navigate('/reporting');
    };

    const handleLinkClick = () => {
      if(hasChanges)
      {
        setOpenGoBackDialog(true);
      }
      else
      {
        navigateToReports();
      }
    
    };

    const closeSnackBar = () => {
      setIsSnackBarOpen(false);
    };

    const setSnackBarProps = (severity: 'error'  | 'warning' | 'success' , message: string) => { 
      setIsSnackBarOpen(true);
      setSnackBarSeverity(severity);
      setSnackbarMessage(message);
    };
    const handleSaveReport = (forceSave = false) => { 
      if (!validateFilters()) {
        setSnackBarProps('error', t('validationErrorRequiredFilters')); 
        return;
      }
 
      const metricsMap = new Map<number, FilterMetricDto>();
 
      filterMetrics?.forEach(m => m.id && metricsMap.set(m.id, m));

      const columnsMap = new Map<number, ColumnMetricDto>();
      columnMetrics?.forEach(m => m.id && columnsMap.set(m.id, m));

      const operatorsMap = new Map<number, FilterOperatorsDto>();
      filterOperator?.forEach(o => o.id && operatorsMap.set(o.id, o)); 
      const validation = ReportValidation.validateReport(
    report!,
    metricsMap,
    columnsMap, 
    filterOperatorsMap,
    logicalOperator,
    filterGroupLinks,
      ); 

      if (!validation.isValid && !forceSave) { 
        setOpenSaveReportModal(true);   
        setCanNotSaveMessage(validation.message);
        return;
      } 
      setLoading(true);
  
      saveReport(selectedStore?.storeNumber, {
        ...report!,
        aggregationId: report?.aggregationId ?? reportAggregationId ?? 1,
      })
        .then(() => {
          if(duplicatedReport)
          { 
            setIsError(true);
            setOpenInformationDialog(true);
            setInformationMessage('A new copy of your report has been created. You can now edit it independently from the original.');
            setInformationModalTitle('Report duplicated successfully');
          }
          else{ 
            navigateToReports();
          }
        })
        .catch((error) => {
          if (typeof(error) === 'string') {
            setIsError(true);
            setSnackBarProps('warning', error); 
          }
          logError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    };
     
    const handleConfirmSave = () => {
      setOpenInformationDialog(false); 
      handleSaveReport(true); 
    };
  
    const handleUpdateReportConfirmed = () => { 
      setLoading(true);
      if(reportId)
      {
        const columnMetricIds = new Set(columnMetrics?.map(metric => metric.id) || []); 
        const validColumns = report?.reportColumns?.filter(column => column.metricId !== 0 && columnMetricIds.has(column.metricId));
        const updatedReport = {
          ...report,
          aggregationId: report?.aggregationId ?? reportAggregationId ?? 1,
          reportColumns: validColumns,  
        } as ReportDto;
        updateReport(reportId, selectedStore?.storeNumber, updatedReport).then(() => {
          navigateToReports();
        }) .catch((error) => {
          if (typeof(error) === 'string') {
            setIsError(true);
            setSnackBarProps('warning', error); 
            setOpenInformationDialog(false);
          }
          logError(error);
        })
          .finally(() => {
            setLoading(false);
          });
         
      }
      setLoading(false);
    };

    const handleUpdateReport = () => {
      if (!validateFilters()) {
        setSnackBarProps('error', t('validationErrorRequiredFilters')); 
        return;
      }
      if(hasChanges)
      {
        const metricsMap = new Map<number, FilterMetricDto>();
 
        filterMetrics?.forEach(m => m.id && metricsMap.set(m.id, m));

        const columnsMap = new Map<number, ColumnMetricDto>();
        columnMetrics?.forEach(m => m.id && columnsMap.set(m.id, m));

        const operatorsMap = new Map<number, FilterOperatorsDto>();
        filterOperator?.forEach(o => o.id && operatorsMap.set(o.id, o)); 
        const validation = ReportValidation.validateReport(
    report!,
    metricsMap,
    columnsMap, 
    filterOperatorsMap,
    logicalOperator,
    filterGroupLinks,
        ); 

        if (!validation.isValid) { 
          setIsError(false);
          setOpenInformationDialog(true);
          setInformationMessage('Are you sure you want to save the changes to this report?' + '\n\n' + validation.message);  
          setInformationModalTitle('confirmUpdateTitle');
          return;
        } else {
          setIsError(false);
          setOpenInformationDialog(true);
          setInformationMessage('confirmUpdate');
          setInformationModalTitle('confirmUpdateTitle');
        }

      }
      else
      {
        handleUpdateReportConfirmed();
      } 
    };


    const handleClose = () => {
      setIsError(false);
      if(isDuplicated)
      {
        navigateToReports();
      }
      setOpenInformationDialog(false);
      setOpenGoBackDialog(false);
      setOpenGenerateDialog(false);
      setOpenSaveReportModal(false);
    }; 
    
    const removeColumn = (index: number) => {
      setColumns(prevColumns => {
        const newColumns = [...prevColumns];
        newColumns.splice(index, 1);
 
        if (newColumns.length === 0) {
          newColumns.push({ id: 0, reportId: 0, metricId: 0 });
        }

        setReport(prev => ({
          ...prev!,
          reportColumns: newColumns,
        }));

        markAsChanged();
        setDisableCreateReport(!isRequestValid());
        return newColumns;
      });
    };


    const handleColumnChange = (index: number, value: string) => {
      const selectedMetric = columnMetrics?.find(option => option.name === value);
      let updatedColumns = [...columns];
 
      updatedColumns[index] = {
        ...updatedColumns[index],
        metricId: selectedMetric?.id ?? 0,
      };
 
      updatedColumns = updatedColumns.filter(c => c.metricId !== 0);
 
      updatedColumns = updatedColumns.map((col, idx) => ({
        ...col,
        id: idx + 1, 
      }));
 
      updatedColumns.push({ id: updatedColumns.length + 1, reportId: 0, metricId: 0 });

      setColumns(updatedColumns);
      setReport(prev => ({
        ...prev!,
        reportColumns: updatedColumns,
      }));

      markAsChanged();
      setDisableCreateReport(!isRequestValid());
    };


    const handleGoBackConfirm = () =>{
      navigateToReports();
    };

    const addFilterGroup = () => {
      setNextUiId(next => next + 1);
      const newFilterGroupId = filterGroups.length ? Math.max(...filterGroups.map(group => group.id!)) + 1 : 1;

      const newFilterGroup: ReportFilterGroupDto = {
        id: newFilterGroupId,
        reportId: report?.id ?? 0,
        reportFilterGroupFilters: [{ id: nextUiId, groupId: newFilterGroupId, filterId: undefined, logicalOperatorId: undefined, filter: { id: 0, reportId: report?.id ?? 0, filterMetricId: 0, operatorId: 0, value: '', uiId: nextUiId } }],
      };

      const updatedGroups = [...filterGroups, newFilterGroup];
      setFilterGroups(updatedGroups);
      if (filterGroups.length > 0) {
        const lastGroup = filterGroups[filterGroups.length - 1];
        const newLink: ReportFilterGroupLinkDto = {
          id: 0,
          reportId: report?.id ?? 0,
          group1Id: lastGroup.id,
          group2Id: newFilterGroupId,
          logicalOperatorId: undefined,
        };
        setFilterGroupLinks(prevLinks => [...prevLinks, newLink]);
        setReport(prevReport => ({
          ...prevReport!,
          reportFilterGroupLinks: [...prevReport?.reportFilterGroupLinks ?? [], newLink],
        }));
      }

      setReport(prevReport => ({
        ...prevReport!,
        reportFilterGroups: updatedGroups,
      }));
    }; 
    const addFilter = (groupId: number | undefined) => {
      const updatedGroups = filterGroups.map(group => {
        if (group.id === groupId) {
          setNextUiId(next => next + 1); 
          const newUiId = nextUiId; 
          return {
            ...group,
            reportFilterGroupFilters: [
              ...group.reportFilterGroupFilters!,
              {
                id: newUiId,
                groupId: group.id,
                filterId: newUiId,
                logicalOperatorId: undefined,
                filter: {
                  id: 0,
                  reportId: 0,
                  filterMetricId: undefined,  
                  operatorId: undefined,
                  value: '',
                  uiId: newUiId,
                },
              },
            ],
          };
        }
        return group;
      }); 
      setFilterGroups(updatedGroups);
    };
 
    const getReasons = (groupId: number | undefined) => {  
      let actionType; 
      const selectedMetric = filterMetrics?.find(option => option.name === 'Transaction Type');

      const transactionTypeFilter = filterGroups?.flatMap(group => 
        group.reportFilterGroupFilters,
      ).find(filterGroupFilter => {
        return filterGroupFilter?.filter?.filterMetricId === selectedMetric?.id && 
           (filterGroupFilter?.groupId === groupId || groupId === 0);
      });

      const transactionTypeValue = transactionTypeFilter?.filter?.value;
      const transaction = transactionTypes.find(x => x.type === transactionTypeValue);

      if (groupId !== 0) {  
        if (transaction?.direction === 'receive') {
          actionType = 'receive';
        } else if (transaction?.direction === 'issue') {
          actionType = 'issue';
        }
      }

      const storeNumber = selectedStore?.storeNumber;
      if (storeNumber) {

        getTransactionReasons(actionType, storeNumber)
          .then((result) => {
            if(groupId != undefined)
            {
              if (result?.data?.reasons) { 
                setGroupReasons(prevReasons => ({
                  ...prevReasons,
                  [groupId]: result.data?.reasons, 
                }));
              }
            }
            else
            {
              const reasons  = result.data?.reasons;
              if(reasons){
                setReasons(reasons); 
              }
            }
       
          })
          .catch((error) => {
            logError(error);
          });
      }
    }; 

    const validateFilters = (): boolean => {
      const invalidEntries: {
            groupId: number;
            filterId?: number; 
            field: FilterField;
            message?: string;
          }[] = [];

      filterGroups?.forEach((group, groupIndex) => {
        const hasMultipleFilters = group.reportFilterGroupFilters!.length > 1;

        group.reportFilterGroupFilters?.forEach((filterGroupFilter, index) => {
          const { filterMetricId, operatorId, value } = filterGroupFilter.filter || {};
          const logicalOperatorId = filterGroupFilter.logicalOperatorId;
          const filterId = filterGroupFilter.filter!.uiId!;
          const groupId = group.id!;  

          const metricDataType = filterMetricId
            ? filterMetrics?.find(metric => metric.id === filterMetricId)?.dataType
            : undefined;
          const groupLink = filterGroupLinks?.find(
            (link) => link.group2Id === group.id,  
          );
          if (!filterMetricId &&  (logicalOperatorId || value ||  groupLink?.logicalOperatorId)) {
            invalidEntries.push({ groupId, filterId, field: 'Metric', message: t('fieldRequired') });
          }
 
          if (!operatorId && (filterMetricId || value)) {
            invalidEntries.push({ groupId, filterId, field: 'Operator', message: t('fieldRequired') });
          }
  
 
          if ( (!value || ((metricDataType === 'decimal' || metricDataType === 'int') && !/\d/.test(value)))  && (filterMetricId || operatorId))
          {
            invalidEntries.push({ groupId, filterId, field: 'Value', message: t('fieldRequired') });
          }
          if (hasMultipleFilters && index > 0 && !logicalOperatorId) {
            invalidEntries.push({ groupId, filterId, field: 'LogicalOperator', message: t('fieldRequired') });
          }
        });
 
        const groupLink = filterGroupLinks?.find(
          (link) => link.group2Id === group.id,  
        );

        if (groupIndex > 0 && !groupLink?.logicalOperatorId)  {
          invalidEntries.push({ groupId: group.id!, field: 'GroupLogicalOperator', message: t('fieldRequired') });
        }
      });  
    
      setInvalidFilters(invalidEntries);

      return invalidEntries.length === 0;  
    };
 
    const removeFilter = (groupId: number | undefined, filterUiId: number) => { 
      markAsChanged();
 
      const updatedGroups = filterGroups.map(group => {
        if (group.id === groupId) {
          const remainingFilters = group.reportFilterGroupFilters!.filter(
            filterGroupFilter => filterGroupFilter.filter!.uiId !== filterUiId,
          );
 
          if (remainingFilters.length === 1) {
            return {
              ...group,
              reportFilterGroupFilters: [remainingFilters[0]],
            };
          }

          return {
            ...group,
            reportFilterGroupFilters: remainingFilters,
          };
        }
        return group;
      });
 
      const group1 = updatedGroups.find(group => group.id === groupId);
      if (group1 && group1.reportFilterGroupFilters!.length === 1) { 
        updatedGroups.forEach(group => {
          if (group.id !== group1.id && group.id! > groupId!) { 
            group.reportFilterGroupFilters = []; 
            removeFilterGroup(group.id);
          }
        });
      }
 
      const finalGroups = updatedGroups.filter(
        group => group.reportFilterGroupFilters!.length > 0,  
      );

      setFilterGroups(finalGroups);
      setReport(prevReport => ({
        ...prevReport,
        reportFilterGroups: finalGroups,
        reportName: reportName,
      }));
    };   
 
    const handleGenerateReport = async () => {
      let hasError = false; 
      if (!fromDate) {
        setFromDateError(true);
        hasError = true;
      } else {
        setFromDateError(false);
      }

      if (!toDate) {
        setToDateError(true);
        hasError = true;
      } else {
        setToDateError(false);
      }
      if (!hasError)
      {
        const columnMetricIds = new Set(columnMetrics?.map(metric => metric.id) || []);
 
        const validColumns = report?.reportColumns!.filter(column => column.metricId !== 0 && columnMetricIds.has(column.metricId));
        const newReportRequest: GenerateReportRequest = {
          reportId: reportId,
          from:  fromDate,
          to: toDate,
          stores: checkedAllStores ? (user?.stores?.map(x=>x.storeNumber as string)?? []) : selectedStores.map(x => x.storeNumber as string),
          reportOnAllAvailableStores: true,
          report: {
            ...report,
            reportColumns: validColumns, 
          },
        }; 

        generateReport(newReportRequest)
          .then((result) => {
            setSnackBarProps('success', result.data.message); 

          }).catch((error) => {
            if (typeof(error) === 'string') {
              setIsError(true);
              setOpenInformationDialog(true);
              setInformationMessage(error);
              setInformationModalTitle('errorSavingData'); 
            }
            logError(error);
          }).finally(() => {
            setOpenGenerateDialog(false);
          });
      }
    };
  
    const getFilteredColumns = (
      columns?: ReportColumnDto[],
      selectedAggregationId?: number,
    ): ReportColumnDto[] | undefined => {
      if (!columns) return undefined;

      const rawOnlyMetrics = [
        'Closing Stock on Hand',
        'Opening Stock on Hand',
        'Theoretical Usage Quantity',
        'Actual Usage Quantity', 
        'Variance - Cost Price',
        'Variance - Quantity',
      ];
      const rawOrDailyOnlyMetrics = ['Date'];
      const startEndDateMetrics = ['Start Date', 'End Date'];
      const currentStockMetric = 'Last Approved Stock on Hand';

      const selectedAggregation = reportAggregations.find(agg => agg.id === selectedAggregationId);
      const aggregationName = selectedAggregation?.name?.toLowerCase();

      const isRaw = aggregationName === 'raw' || aggregationName === undefined;
      const isDailyTotal = aggregationName === 'daily total';
      const isTotal = aggregationName === 'total';
      const shouldHideStartEnd = isRaw || isDailyTotal || isTotal;
      const shouldHideDate = !isRaw && !isDailyTotal;

      const filteredColumns = columns.filter(column => {
        const metricName = columnMetrics?.find(metric => metric.id === column.metricId)?.name;
        if (!metricName) return true;

        if (!isRaw && rawOnlyMetrics.includes(metricName)) return false;
        if (!isRaw && metricName === currentStockMetric) return false;
        if (shouldHideStartEnd && startEndDateMetrics.includes(metricName)) return false;
        if (shouldHideDate && rawOrDailyOnlyMetrics.includes(metricName)) return false;

        return true;
      });

      setReport(prevReport => ({
        ...prevReport,
        aggregationId: selectedAggregationId,
        reportColumns: filteredColumns,
      }));

      return filteredColumns;
    };

    const handleDataGroupChange = (dataGroupId: number) => {
      setSelectedDataGroup(dataGroupId);
      
      // Fetch column metrics for the new data group and current aggregation
      // This single call will handle filtering and setting columnMetrics
      getColumnMetricsList(reportAggregationId ?? 1, dataGroupId);
      
      // Filter existing filter metrics based on new data group (exclude date fields - user selects dates when generating report)
      if (allFilterMetrics) {
        const excludedDateFields = ['date', 'planned count date', 'delivery date', 'start date', 'end date'];
        const filtered = allFilterMetrics.filter(
          (metric) => !excludedDateFields.includes(metric.name?.toLowerCase() ?? '') && (metric.isDefaultField || metric.dataGroupId === dataGroupId),
        );
        setFilterMetrics(filtered);
      }

      // Clear existing columns and filters as they may be incompatible with the new data group
      setColumns([]);
      // Allow default-columns to be re-initialized for the newly selected data group
      setDidInitColumns(false);
      setFilterGroups([]);
      
      markAsChanged();
      
      // Update report object
      setReport((prevReport) => ({
        ...prevReport,
        dataGroupId,
      }));
    };

    const handleAggregateChange = (value: number) => {
      setReportAggregationId(value);   
      getFilteredColumns(columns, value);
      // getFilteredColumns updates report.reportColumns, columns state syncs via useEffect
      markAsChanged();
      getColumnMetricsList(value);
    };   
 
    const handleGroupLogicalOperatorChange = (
      groupId: number | undefined,
      value: string,
    ) => {
      const operatorId = parseInt(value, 10);
 
      const updatedLinks: ReportFilterGroupLinkDto[] = filterGroupLinks ? [...filterGroupLinks] : [];

      const existingLinkIndex = updatedLinks.findIndex(link => link.group2Id === groupId);

      if (existingLinkIndex !== -1) {
        updatedLinks[existingLinkIndex] = {
          ...updatedLinks[existingLinkIndex],
          logicalOperatorId: operatorId,
        };
      } else {
        const previousGroupId = filterGroups[filterGroups.findIndex(g => g.id === groupId) - 1]?.id;

        if (!previousGroupId || !report?.id || !groupId) return;

        const newLink: ReportFilterGroupLinkDto = {
          id: 0,
          reportId: report.id,
          group1Id: previousGroupId,
          group2Id: groupId,
          logicalOperatorId: operatorId,
        };

        updatedLinks.push(newLink);
      }

      setFilterGroupLinks(updatedLinks);
 
      const updatedGroups = filterGroups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            groupLogicalOperatorId: operatorId,
          };
        }
        return group;
      });

      setFilterGroups(updatedGroups); 
      setReport(prevReport => ({
        ...prevReport!,
        reportFilterGroups: updatedGroups,
        reportFilterGroupLinks: updatedLinks,
      }));
 
      setInvalidFilters((prevInvalidFilters) =>
        updateInvalidFilters(prevInvalidFilters, groupId!, undefined, 'GroupLogicalOperator', value),
      );

      markAsChanged();
    };
 
    const removeFilterGroup = (groupId: number | undefined) => {
      markAsChanged();
      const updatedGroups = filterGroups.filter(group => group.id !== groupId);
      setFilterGroups(updatedGroups);

      const updatedLinks = filterGroupLinks.filter(
        link => link.group1Id !== groupId && link.group2Id !== groupId,
      );
      setFilterGroupLinks(updatedLinks);
 
      setReport((prevReport) => ({
        ...prevReport,
        reportFilterGroups: updatedGroups,
        reportName: reportName,
        reportFilterGroupLinks: updatedLinks,
      }));
    };

    const handleReportChange = (event: ChangeEvent<HTMLInputElement>) => { 
      setReportName(event.target.value);
      setDisableCreateReport(!((event.target.value.trim() !=='' && columns.length > 0  && columns.some(column => column.metricId !== 0))));
      setReport((prevReport) => ({
        ...prevReport, reportName: event.target.value,
      }));
      markAsChanged();
    };
 
    const getMaxDate = () => {
      const today = new Date().toISOString().split('T')[0];
      return today;
    };

    const getMinDate = () => {
      return new Date('1900-01-01').toISOString().split('T')[0]; 
    }; 

    const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
      setSelectedStores(value);
    };

    const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      setSelectedStores([]);

      setCheckedAllStores(checked);
    };

    const loadCategories = async (storeNumber: string) => {
      try {
        const res = await getPrimaryCategoriesForStore(storeNumber);
        if (res.data) {
          setAllCategories(res.data);
        }
      } catch (error) {
        logError(error);
      }
    };
   
    const updateFilter = async (
      filterGroupFilter: ReportFilterGroupFilterDto,
      field: string,
      value: string,
    ): Promise<ReportFilterGroupFilterDto> => {
      const updatedFilter = { ...filterGroupFilter.filter, [field]: value };

      if (field === 'filterMetricId') {
        const selectedMetric = filterMetrics?.find(option => option.id === parseInt(value));

        if (selectedMetric) {
          updatedFilter.filterMetricId = selectedMetric.id;
          updatedFilter.value = undefined;
          
          // Use operators from the pre-loaded map instead of making an API call
          const operators = filterOperatorsMap.get(selectedMetric.id!) || [];
          
          // If operators aren't loaded yet for this metric, fall back to API call
          if (operators.length === 0) {
            const response = await getOperatorsForMetric(selectedMetric.fieldType!);
            const fetchedOperators = response.data || [];
            setFilterOperatorsMap(prevMap => {
              const newMap = new Map(prevMap);
              if (updatedFilter.filterMetricId) {
                newMap.set(updatedFilter.filterMetricId, fetchedOperators);
              }
              return newMap;
            });
            if (fetchedOperators.length > 0) {
              updatedFilter.operatorId = fetchedOperators[0].id;
            }
          } else {
            if (operators.length > 0) {
              updatedFilter.operatorId = operators[0].id;
            }
          }
          
          if (selectedMetric.name === 'Transaction Type') {
            setTimeout(getReasons, 0);
          }
        }
      } else if (field === 'operatorId') {
        if (filterGroupFilter.filter!.filterMetricId) {
          const selectedOperator = filterOperatorsMap
            .get(filterGroupFilter.filter!.filterMetricId)
            ?.find(option => option.id === parseInt(value));
          if (selectedOperator) {
            updatedFilter.operatorId = selectedOperator.id;
          }
        }
      }
      return { ...filterGroupFilter, filter: updatedFilter };
    };

    const updateGroupFilters = async (
      group: ReportFilterGroupDto,
      filterUiId: number | undefined,
      field: string,
      value: string,
    ): Promise<ReportFilterGroupFilterDto[]> => {
      return Promise.all(
        group.reportFilterGroupFilters?.map(filterGroupFilter =>
      filterGroupFilter.filter!.uiId === filterUiId
        ? updateFilter(filterGroupFilter, field, value)
        : Promise.resolve(filterGroupFilter),
        ) || [],
      );
    };

    const updateGroups = async (
      filterGroups: ReportFilterGroupDto[],
      groupId: number | undefined,
      filterUiId: number | undefined,
      field: string,
      value: string,
    ): Promise<ReportFilterGroupDto[]> => {
      return Promise.all(
        filterGroups.map(async group =>
          group.id === groupId
            ? {
              ...group,
              reportFilterGroupFilters: await updateGroupFilters(group, filterUiId, field, value),
            }
            : group,
        ),
      );
    };

    const updateFilterGroups = (
      groupId: number | undefined,
      filterUiId: number | undefined,
      operatorId: number,
    ): ReportFilterGroupDto[] | undefined => {
      return filterGroups?.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            reportFilterGroupFilters: updateFilterGroupFilters(group, filterUiId, operatorId),
            groupLogicalOperatorId: operatorId,
          };
        }
        return group;
      });
    };

    const updateFilterGroupFilters = (
      group: ReportFilterGroupDto,
      filterUiId: number | undefined,
      operatorId: number,
    ): ReportFilterGroupFilterDto[] | undefined => {
      return group.reportFilterGroupFilters?.map(filterGroupFilter => {
        if (filterGroupFilter.filter?.uiId === filterUiId) {
          const selectedLogicalOperator = logicalOperator?.find(option => option.id === operatorId);
          return {
            ...filterGroupFilter,
            logicalOperatorId: selectedLogicalOperator ? selectedLogicalOperator.id : undefined,
          };
        }
        return filterGroupFilter;
      });
    };

    const updateInvalidFilters = (
      prevInvalidFilters: Array<{ groupId: number; filterId?: number; field: 'Metric' | 'Operator' | 'Value' | 'LogicalOperator' | 'GroupLogicalOperator'; message?: string; }>,
      groupId: number,
      filterId: number | undefined,
      field: 'Metric' | 'Operator' | 'Value' | 'LogicalOperator' | 'GroupLogicalOperator',
      value: string,
      message?: string,
    ) => { 
      const updatedInvalidFilters = prevInvalidFilters.filter(
        (invalid) => !(invalid.groupId === groupId && invalid.filterId === filterId && invalid.field === field),
      );
 
      if (!value) {
        updatedInvalidFilters.push({
          groupId,
          filterId,
          field,
          message: message || t('fieldRequired'),
        });
      }

      return updatedInvalidFilters;
    };


    const updateReportWithGroups = (updatedGroups: ReportFilterGroupDto[] | undefined) => {
      setReport(prevReport => ({
        ...prevReport,
        reportFilterGroups: updatedGroups,
        reportName: reportName,
      }));
    };
    const handleFilterLogicalOperatorChange= (groupId: number | undefined, filterUiId: number | undefined, value: string) => {
      const operatorId = parseInt(value, 10);
      markAsChanged();

      const updatedGroups = updateFilterGroups(groupId, filterUiId, operatorId )?? [];  
      setFilterGroups(updatedGroups);
      updateReportWithGroups(updatedGroups);  
      setInvalidFilters((prevInvalidFilters) =>
        updateInvalidFilters(prevInvalidFilters, groupId!, filterUiId, 'LogicalOperator', value),
      );

    }; 

    const handleFilterChange = async (
      groupId: number | undefined,
      filterUiId: number | undefined,
      field: string,
      value: string,
    ) => {  
      const updatedGroups = await updateGroups(
        filterGroups,
        groupId,
        filterUiId,
        field,
        value,
      );

      setFilterGroups(updatedGroups);
      setReport(prevReport => ({
        ...prevReport,
        reportFilterGroups: updatedGroups,
        reportName: reportName,
      }));
      markAsChanged();

      const fieldMapping: Record<string, 'Metric' | 'Operator' | 'Value'> = {
        filterMetricId: 'Metric',
        operatorId: 'Operator',
        value: 'Value',
      };
 
      const typedField = fieldMapping[field];
      setInvalidFilters((prevInvalidFilters) =>
        updateInvalidFilters(prevInvalidFilters, groupId!, filterUiId, typedField, value),
      ); 
    };

    const markAsChanged  = () => {
      setHasChanges(true);
    };  

    const blocker = useBlocker(
      ({ currentLocation, nextLocation }) => {
        if (isNavigatingRef.current) return false;
        return hasChanges && currentLocation.pathname !== nextLocation.pathname;
      },
    );

    useEffect(() => {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (hasChanges) {
          event.preventDefault();
          event.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);
 
    return {
      t,
      dataGroups,
      selectedDataGroup,
      columnMetrics,
      columns,
      reportName,
      filterGroups,
      filterMetrics,
      logicalOperator,
      filterOperator,
      report,
      filterGroupLinks,
      disableCreateReport,
      openInformationDialog,
      informationMessage,
      isEdit,
      setReport,
      getColumnMetricsList,
      setColumns,
      setColumnMetrics,
      handleColumnChange,
      removeColumn,
      addFilterGroup,
      addFilter,
      removeFilter,
      handleFilterChange,
      handleFilterLogicalOperatorChange,
      handleGroupLogicalOperatorChange,
      removeFilterGroup,
      handleReportChange,
      getLogicalOperatorsList,
      getFilterMetricsList,
      getFilterOperatorList,
      handleSaveReport,
      fetchReportData,
      getMetricNameById,
      handleAddFilterGroup,
      handleClose,
      handleUpdateReport,
      handleLinkClick,
      openGoBackDialog,
      handleGoBackConfirm, 
      fromDate,
      toDate,
      getMaxDate,
      getMinDate,
      openGenerate,
      openGenerateDialog,
      user,
      handleSelectChange,
      handleSelectAllChange,
      selectedStores,
      checkedAllStores,
      reportAggregations,
      handleDataGroupChange,
      handleAggregateChange,
      reportAggregationId,
      loading,
      handleGenerateReport,
      allCategories, 
      transactionTypes,
      reasons,
      filterOperatorsMap,
      isMobile,
      isError,
      informationModalTitle,
      handleUpdateReportConfirmed,
      groupReasons,
      isGenerateDisabled,
      ordersType, 
      invalidFilters,
      closeSnackBar,
      isSnackBarOpen,
      snackBarSeverity,
      snackbarMessage, 
      setFromDate,
      setFromDateError,
      fromDateError,
      setToDate,
      setToDateError,
      toDateError,
      hasChanges,
      selectedStore, 
      hasFilterConflict,  
      handleConfirmSave,
      openSaveReportModal,
      canNotSaveMessage,
      isDuplicated,
      navigateToReports,
      markAsChanged,
      blocker,
    };
};
